import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import styles from './spin-wheel.module.scss'

const SpinWheel = ({
    participants,
    totalAmount,
    predeterminedWinner,
    onClose,
    onSpinComplete,
    onSpinStart,
}) => {
    const [isSpinning, setIsSpinning] = useState(false)
    const [spinComplete, setSpinComplete] = useState(false)
    const [winner, setWinner] = useState(null)
    const [finalRotation, setFinalRotation] = useState(0)
    const wheelRef = useRef(null)

    // Generate wheel sections based on participants
    const generateSections = () => {
        let currentAngle = 0
        return participants.map((participant, index) => {
            const percentage =
                totalAmount > 0 ? participant.amount / totalAmount : 1 / participants.length
            const angle = percentage * 360
            const section = {
                participant,
                percentage,
                startAngle: currentAngle,
                endAngle: currentAngle + angle,
                angle,
                color: `hsl(${(index * 137.5) % 360}, 70%, 65%)`, // Golden ratio for nice color distribution
            }
            currentAngle += angle
            return section
        })
    }

    const sections = generateSections()

    const handleSpin = () => {
        if (isSpinning || participants.length === 0) return

        setIsSpinning(true)

        // Notify parent that spin is starting
        if (onSpinStart) {
            onSpinStart()
        }

        // Find the predetermined winner index
        let winnerIndex = 0
        if (predeterminedWinner) {
            console.log('Predetermined winner from server:', predeterminedWinner)
            console.log('All participants:', participants)

            winnerIndex = participants.findIndex(
                p =>
                    p.userId === predeterminedWinner.userId ||
                    p.userId === predeterminedWinner.user_id ||
                    p.userName === predeterminedWinner.userName ||
                    p.userName === predeterminedWinner.user_name,
            )

            console.log('Found winner at index:', winnerIndex)
            if (winnerIndex >= 0) {
                console.log('Matched participant:', participants[winnerIndex])
            }

            // Fallback to first participant if not found
            if (winnerIndex === -1) {
                console.warn('Predetermined winner not found in participants:', predeterminedWinner)
                winnerIndex = 0
            }
        }

        // Calculate rotation to land on the predetermined winner
        const winnerSection = sections[winnerIndex]
        const winnerSectionCenterSVG = winnerSection.startAngle + winnerSection.angle / 2

        // Debug all sections with their initial CSS positions
        console.log('All sections:')
        sections.forEach((section, i) => {
            const centerSVG = section.startAngle + section.angle / 2
            const initialCssAngle = (centerSVG - 270 + 360) % 360
            console.log(
                `  Section ${i}: ${section.participant.userName}, ` +
                    `${(section.percentage * 100).toFixed(1)}%, ` +
                    `SVG Angle: ${centerSVG.toFixed(1)}°, ` +
                    `Initial CSS Angle (from top CW): ${initialCssAngle.toFixed(1)}°`,
            )
        })

        // Calculate current visual position of winner section
        const currentWinnerPositionCSS = (winnerSectionCenterSVG - 270 + 360) % 360

        console.log('Winner Section Positioning:')
        console.log(`  Winner section center (SVG): ${winnerSectionCenterSVG.toFixed(1)}°`)
        console.log(
            `  Current visual position: ${currentWinnerPositionCSS.toFixed(1)}° clockwise from top`,
        )
        console.log(`  Target: 0° (top under pointer)`)

        // Calculate rotation needed to bring winner to top (0°)
        const targetCssRotation = (360 - currentWinnerPositionCSS) % 360

        console.log(`  Required rotation: ${targetCssRotation.toFixed(1)}° clockwise`)
        console.log(
            `  Verification: ${currentWinnerPositionCSS.toFixed(1)}° + ${targetCssRotation.toFixed(
                1,
            )}° = ${(currentWinnerPositionCSS + targetCssRotation) % 360}° (should be 0°)`,
        )

        const fullRotations = 5 // Fixed number for consistent testing
        const finalAngle = fullRotations * 360 + targetCssRotation

        setFinalRotation(finalAngle)

        // Complete spin after animation
        setTimeout(() => {
            setIsSpinning(false)
            setSpinComplete(true)
            setWinner(participants[winnerIndex])
            if (onSpinComplete) {
                onSpinComplete(participants[winnerIndex])
            }
        }, 4000) // 4 second spin animation
    }

    const handleDone = () => {
        onClose()
    }

    const generateWheelSVG = () => {
        const radius = 150
        const centerX = 160
        const centerY = 160
        const svgSize = 320

        return (
            <svg
                viewBox={`0 0 ${svgSize} ${svgSize}`}
                className={styles.wheelSvg}
                preserveAspectRatio="xMidYMid meet"
                style={{ overflow: 'visible' }}
            >
                {/* Draw sections */}
                {sections.map((section, index) => {
                    const startAngleRad = (section.startAngle * Math.PI) / 180
                    const endAngleRad = (section.endAngle * Math.PI) / 180

                    const x1 = centerX + radius * Math.cos(startAngleRad)
                    const y1 = centerY + radius * Math.sin(startAngleRad)
                    const x2 = centerX + radius * Math.cos(endAngleRad)
                    const y2 = centerY + radius * Math.sin(endAngleRad)

                    const largeArcFlag = section.angle > 180 ? 1 : 0

                    const pathData = [
                        `M ${centerX} ${centerY}`,
                        `L ${x1} ${y1}`,
                        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                        'Z',
                    ].join(' ')

                    // Calculate text position with margin from slice borders
                    const textAngle = (section.startAngle + section.endAngle) / 2
                    const textAngleRad = (textAngle * Math.PI) / 180
                    const textRadius = radius * 0.65 // Increased from 0.7 to give more margin
                    const textX = centerX + textRadius * Math.cos(textAngleRad)
                    const textY = centerY + textRadius * Math.sin(textAngleRad)

                    return (
                        <g key={index} style={{ overflow: 'visible' }}>
                            <path d={pathData} fill={section.color} stroke="#fff" strokeWidth="2" />
                            <text
                                x={textX}
                                y={textY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#333"
                                fontSize="14"
                                fontWeight="700"
                                transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                                style={{ overflow: 'visible', whiteSpace: 'nowrap' }}
                            >
                                @{section.participant.userName}
                            </text>
                        </g>
                    )
                })}

                {/* Center circle */}
                <circle
                    cx={centerX}
                    cy={centerY}
                    r="16"
                    fill="#333"
                    stroke="#fff"
                    strokeWidth="3"
                />
            </svg>
        )
    }

    return ReactDOM.createPortal(
        <div className={styles.overlay} onClick={onClose}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-label="Raffle wheel"
                onClick={event => event.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2>Raffle Wheel</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className={styles.modalContent}>
                    <div className={styles.wheelContainer}>
                        {/* Pointer */}
                        <div className={styles.pointer}>▼</div>

                        {/* Wheel */}
                        <div
                            ref={wheelRef}
                            className={styles.wheel}
                            style={{
                                transform: `rotate(${finalRotation}deg)`,
                                transition: isSpinning
                                    ? 'transform 4s cubic-bezier(0.25, 0.1, 0.25, 1)'
                                    : 'none',
                            }}
                        >
                            {generateWheelSVG()}
                        </div>

                        {/* Winner announcement overlay */}
                        {spinComplete && winner && (
                            <div className={styles.winnerOverlay}>
                                <div className={styles.winnerAnnouncement}>
                                    <h3 className={styles.winnerTitle}>WINNER!</h3>
                                    <div className={styles.winnerRow}>
                                        <div className={styles.winnerEmojis}>
                                            <span>🎉</span>
                                            <span>🎊</span>
                                        </div>
                                        <div className={styles.winnerName}>@{winner.userName}</div>
                                        <div className={styles.winnerEmojis}>
                                            <span>✨</span>
                                            <span>🎉</span>
                                        </div>
                                    </div>
                                    <div className={styles.winnerChance}>
                                        {/* winner.amount is net amount since winner comes from participants */}
                                        {totalAmount > 0
                                            ? ((winner.amount / totalAmount) * 100).toFixed(1)
                                            : '100'}
                                        % chance
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.controls}>
                        <button
                            className={styles.spinButton}
                            onClick={spinComplete ? handleDone : handleSpin}
                            disabled={isSpinning || (!spinComplete && participants.length === 0)}
                        >
                            {isSpinning ? 'Spinning...' : spinComplete ? 'Done' : 'Spin the Wheel!'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    )
}

export default SpinWheel
